// src/pages/lead/LeadDashboard.jsx
import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { formatTime } from "../../hooks/useFirestore";
import { useLeadData } from "../../context/LeadContext";
import { LEAD_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import AttendanceCard from "../../components/attendance/AttendanceCard";
import { isEmployeeVisible, formatTimeStr } from "../../utils/meetings";
import { useUpcomingHolidays } from "../../hooks/useFirestoreExtensions";
import { isPresent } from "../../utils/attendanceStatus";

function calcHours(from, to) {
  return (to - from) / 3_600_000;
}

export default function LeadDashboard() {
  const { userProfile } = useAuth();
  const uid = userProfile?.uid;

  // All data comes from LeadContext — no duplicate listeners created here
  const {
    members,
    allAttendance,
    tasks,
    announcements,
    todayMeetings: rawTodayMeetings,
    stats,
  } = useLeadData();

  const todayMeetings = (rawTodayMeetings || []).filter((m) => isEmployeeVisible(m, userProfile));
  const { data: upcomingHolidays } = useUpcomingHolidays();

  const {
    presentDays,
    attendRate,
    pendingTasks,
    completedTasks,
    teamOnLeave,
    pendingApprovals,
  } = stats;

  const memberIds = new Set(members.map((m) => m.uid));
  const teamAtt = allAttendance.filter((a) => memberIds.has(a.userId));
  // ── Popup State ─────────────────────────
const [showPopup, setShowPopup] = useState(false);
const [popupTitle, setPopupTitle] = useState("");
const [popupData, setPopupData] = useState([]);

// ── Popup Handler ───────────────────────
function openPopup(title, data) {
  setPopupTitle(title);
  setPopupData(data);
  setShowPopup(true);
}

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <Layout navItems={LEAD_NAV} panelLabel="Team Lead Panel">
      <div className="page-header">
        <div>
          <h1 className="page-title">Team Dashboard</h1>
          <p className="page-subtitle">{today}</p>
        </div>
        <div className="user-greeting">
          <span>Hello, {userProfile?.displayName?.split(" ")[0]} </span>
        </div>
      </div>

      
<div className="kpi-grid">
  {/* Team Present */}
  <div
    className="kpi-card kpi-card--blue"
    style={{ cursor: "pointer" }}
    onClick={() =>
      openPopup(
        "Present Employees",
        members.filter((m) =>
          teamAtt.some(
            (a) =>
              a.userId === m.uid &&
              isPresent(a),
          ),
        ),
      )
    }
  >
    <div className="kpi-icon">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="22"
        height="22"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
        />
      </svg>
    </div>

    <div className="kpi-body">
      <span className="kpi-value">{presentDays}</span>
      <span className="kpi-label">Team Present</span>
      <span className="kpi-sub">Today</span>
    </div>
  </div>

  {/* Attendance Rate */}
  <div className="kpi-card kpi-card--green">
    <div className="kpi-icon">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="22"
        height="22"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z"
        />
      </svg>
    </div>

    <div className="kpi-body">
      <span className="kpi-value">{attendRate}%</span>
      <span className="kpi-label">Attendance Rate</span>
      <span className="kpi-sub">Today</span>
    </div>
  </div>

  {/* Pending Tasks */}
  <div
    className="kpi-card kpi-card--amber"
    style={{ cursor: "pointer" }}
    onClick={() =>
      openPopup(
        "Pending Tasks",
        tasks.filter((t) => t.status !== "done"),
      )
    }
  >
    <div className="kpi-icon">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="22"
        height="22"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
    </div>

    <div className="kpi-body">
      <span className="kpi-value">{pendingTasks}</span>
      <span className="kpi-label">Team Pending Tasks</span>
      <span className="kpi-sub">
        {completedTasks} completed
      </span>
    </div>
  </div>

  {/* Pending Leaves */}
  <div
    className="kpi-card kpi-card--purple"
    style={{ cursor: "pointer" }}
    onClick={() =>
      openPopup(
        "Pending Leave Requests",
        members.filter(
          (m) => m.leaveStatus === "pending",
        ),
      )
    }
  >
    <div className="kpi-icon">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="22"
        height="22"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
    </div>

    <div className="kpi-body">
      <span className="kpi-value">
        {pendingApprovals}
      </span>
      <span className="kpi-label">
        Pending Leaves
      </span>
      <span className="kpi-sub">
        Awaiting review
      </span>
    </div>
  </div>

  {/* Team On Leave */}
  <div
    className="kpi-card kpi-card--blue"
    style={{ cursor: "pointer" }}
    onClick={() =>
      openPopup(
        "Employees On Leave",
        members.filter(
          (m) => m.leaveStatus === "approved",
        ),
      )
    }
  >
    <div className="kpi-icon">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="22"
        height="22"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
        />
      </svg>
    </div>

    <div className="kpi-body">
      <span className="kpi-value">
        {teamOnLeave}
      </span>
      <span className="kpi-label">
        Team on Leave
      </span>
      <span className="kpi-sub">Today</span>
    </div>
  </div>
</div>

{/* ── Popup Modal ───────────────────────────── */}
{showPopup && (
  <div
    onClick={() => setShowPopup(false)}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.4)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: "#fff",
        width: "450px",
        maxHeight: "500px",
        overflowY: "auto",
        borderRadius: "16px",
        padding: "1.5rem",
        boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <h3>{popupTitle}</h3>

        <button
          onClick={() => setShowPopup(false)}
          style={{
            border: "none",
            background: "transparent",
            fontSize: "1.2rem",
            cursor: "pointer",
          }}
        >
          ✕
        </button>
      </div>

      {popupData.length === 0 ? (
        <div className="card-empty">
          No data found.
        </div>
      ) : (
        popupData.map((item, index) => (
          <div
            key={item.uid || item.id || index}
            style={{
              padding: "0.9rem",
              borderBottom: "1px solid #eee",
            }}
          >
            {/* Employee Data */}
            {item.displayName && (
              <>
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: "0.2rem",
                  }}
                >
                  {item.displayName}
                </div>

                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#666",
                  }}
                >
                  {item.department ||
                    "No Department"}
                </div>
              </>
            )}

            {/* Task Data */}
            {item.title && (
              <>
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: "0.3rem",
                  }}
                >
                  {item.title}
                </div>

                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#666",
                  }}
                >
                  Assigned to:{" "}
                  {item.assignedToName || "—"}
                </div>

                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#999",
                    marginTop: "0.2rem",
                  }}
                >
                  Status:{" "}
                  {item.status?.replace("_", " ")}
                </div>
              </>
            )}
          </div>
        ))
      )}
    </div>
  </div>
)}

      <div className="dashboard-grid">
        {/* Attendance widget */}
        <AttendanceCard userId={uid} userProfile={userProfile} />

        {/* Team Today */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Team Today</h3>
          </div>
          {members.length === 0 ? (
            <div className="card-empty">No team members.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {members.map((m) => {
                const att = teamAtt.find((a) => a.userId === m.uid);
                const tActiveBreak = att?.breaks?.find((b) => !b.endTime);
                const tLiveHours =
                  att?.checkIn && !att?.checkOut
                    ? calcHours(att.checkIn.toDate(), new Date())
                    : att?.hoursWorked || 0;
                return (
                  <div
                    key={m.uid}
                    style={{
                      borderBottom: "1px solid var(--border)",
                      paddingBottom: "0.75rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <strong>{m.displayName}</strong>
                      <span
                        style={{
                          textTransform: "capitalize",
                          fontSize: "0.85rem",
                          color: "var(--text-muted)",
                        }}
                      >
                        {att?.mode || "—"}
                      </span>
                    </div>
                    <div
                      className="attendance-status-grid"
                      style={{ marginBottom: tActiveBreak ? "0.5rem" : "0" }}
                    >
                      <div className="att-stat">
                        <span className="att-stat-label">Punch In</span>
                        <span className="att-stat-value">
                          {att ? formatTime(att.checkIn) : "—"}
                        </span>
                      </div>
                      <div className="att-stat">
                        <span className="att-stat-label">Punch Out</span>
                        <span className="att-stat-value">
                          {att?.checkOut ? formatTime(att.checkOut) : "—"}
                        </span>
                      </div>
                      <div className="att-stat">
                        <span className="att-stat-label">Hours</span>
                        <span className="att-stat-value">
                          {tLiveHours > 0 ? `${tLiveHours.toFixed(1)}h` : "—"}
                        </span>
                      </div>
                      <div className="att-stat">
                        <span className="att-stat-label">Status</span>
                        <span
                          className={`status-pill status-pill--${isPresent(att) ? "green" : "gray"}`}
                        >
                          {att ? att.status : "absent"}
                        </span>
                      </div>
                    </div>
                    {tActiveBreak && (
                      <div
                        style={{
                          padding: "0.5rem",
                          backgroundColor: "#fff3cd",
                          borderRadius: "0.5rem",
                          fontSize: "0.8rem",
                          color: "#856404",
                          display: "flex",
                          justifyContent: "space-between",
                          marginTop: "0.5rem",
                        }}
                      >
                        <span>
                          ☕{" "}
                          {tActiveBreak.breakType === "tea1"
                            ? "Tea Break 1"
                            : tActiveBreak.breakType === "tea2"
                              ? "Tea Break 2"
                              : "Lunch Break"}
                        </span>
                        <span>Active</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Team tasks */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Team Tasks</h3>
            <a href="/lead/tasks" className="card-link">
              View all →
            </a>
          </div>
          {tasks.length === 0 ? (
            <div className="card-empty">No team tasks assigned.</div>
          ) : (
            <div className="task-list">
              {tasks.slice(0, 4).map((t) => (
                <div key={t.id} className="task-row">
                  <div className="task-row-info">
                    <span className="task-row-title">{t.title}</span>
                    <span className="task-row-meta">
                      to {t.assignedToName} · due{" "}
                      {t.deadline?.toDate?.()?.toLocaleDateString?.("en-IN") || "—"}
                    </span>
                  </div>
                  <span
                    className={`status-pill status-pill--${
                      t.status === "COMPLETED"
                        ? "green"
                        : t.status === "IN_PROGRESS"
                          ? "blue"
                          : "gray"
                    }`}
                  >
                    {t.status.replace("_", " ")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Today's meetings — from context, no extra listener */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Today's Meetings</h3>
            <span className="card-badge">{todayMeetings.length}</span>
          </div>
          {todayMeetings.length === 0 ? (
            <div className="card-empty">No more meetings today.</div>
          ) : (
            <div className="meetings-list">
              {todayMeetings.slice(0, 4).map((m) => (
                <div key={m.id} className="meeting-row">
                  <div className="meeting-dot" />
                  <div className="meeting-info">
                    <span className="meeting-title">{m.title}</span>
                    <span className="meeting-time">
                      {m.date} · {formatTimeStr(m.startTime || m.time)}{m.endTime && ` - ${formatTimeStr(m.endTime)}`}
                    </span>
                  </div>
                  {m.meetLink && (() => {
                    const now = new Date();
                    const start = new Date(`${m.date}T${m.startTime || m.time}`);
                    const joinWindow = new Date(start.getTime() - 10 * 60 * 1000);
                    return now >= joinWindow ? (
                      <a
                        href={m.meetLink}
                        target="_blank"
                        rel="noreferrer"
                        className="meeting-join-btn"
                      >
                        Join
                      </a>
                    ) : (
                      <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{formatTimeStr(m.startTime || m.time)}</span>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Announcements */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Announcements</h3>
          </div>
          {announcements.length === 0 ? (
            <div className="card-empty">No announcements.</div>
          ) : (
            <div className="announcements-list">
              {announcements.slice(0, 3).map((a) => (
                <div
                  key={a.id}
                  className={`announcement-row priority-${a.priority}`}
                >
                  <span className="announcement-priority-dot" />
                  <div className="announcement-content">
                    <span className="announcement-title">{a.title}</span>
                    <span className="announcement-msg">{a.message}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Holidays */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Upcoming Holidays</h3>
            <span className="card-badge">{upcomingHolidays.length}</span>
          </div>
          {upcomingHolidays.length === 0 ? (
            <div className="card-empty">No upcoming holidays scheduled.</div>
          ) : (
            <div className="announcements-list">
              {upcomingHolidays.slice(0, 5).map((h) => {
                const dt = new Date(h.date + "T12:00:00");
                const daysLeft = Math.ceil((dt - new Date()) / 86400000);
                const typeColor = h.type === "public" ? "green" : h.type === "optional" ? "blue" : "amber";
                return (
                  <div key={h.id} className="announcement-row">
                    <span className="announcement-priority-dot" />
                    <div className="announcement-content">
                      <span className="announcement-title">🎉 {h.name}</span>
                      <span className="announcement-msg">
                        {dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        {" · "}
                        <span className={`status-pill status-pill--${typeColor}`} style={{ fontSize: "0.65rem" }}>{h.type}</span>
                        {" · "}
                        {daysLeft === 1 ? "Tomorrow!" : `in ${daysLeft} days`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
